import * as React from 'react';
import { useApiRequest } from '../../../hooks/useApiRequest';
import { api } from '../../../lib/api';
import { Packet } from '../../../types/packet.schema';
import { noop } from '../../../utils';
import { DataNotFound } from '../../common/Errors/DataNotFound';
import { PageLoading } from '../../common/Loading/PageLoading';
import { SellersPacket } from '../../../types/sellersPacket.schema';

type PacketLoaderProps = {
  packetId: string;
  sourceComponent?: string;
  sellersPacket?: SellersPacket;
  children?: (packet: Packet) => JSX.Element;
};

// The PacketLoader loads a packet and filters out any questions taht already have an answer in
// a sellersPacket (if given)
const PacketLoader = ({
  children,
  packetId,
  sellersPacket
}: PacketLoaderProps): JSX.Element => {
  const {
    request: getPacket,
    data: packet,
    isLoading,
    isIdle,
    isError
  } = useApiRequest<Record<string, unknown>, Packet>({
    method: 'GET',
    endpoint: api.unauthenticated.packet,
    transform: (packet: Packet) => filterAnsweredQuestions(packet, sellersPacket)
  });

  React.useEffect(() => {
    getPacket({ endpointParams: [packetId] }).catch(noop);
  }, [getPacket, packetId]);

  if (isLoading || isIdle) {
    return <PageLoading />;
  }

  if (isError) {
    return <DataNotFound itemName="packet" />;
  }
  return <>{children(packet)}</>;
};

export { PacketLoader };

const filterAnsweredQuestions = (packet: Packet, sellersPacket: SellersPacket) => {
  if (!sellersPacket?.answers || sellersPacket.answers.length === 0) return packet;
  const answeredQuestionIds = sellersPacket.answers.map(answer => answer.questionId);
  const filteredQuestions = packet.questions.filter(
    question => !answeredQuestionIds.includes(question.id)
  );
  return { ...packet, questions: filteredQuestions };
};
